import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Grid, TextField, Button, MenuItem, Snackbar, Alert } from "@mui/material";
import Menu from "./Menu"; // Import your Menu component here
import SearchIcon from "@mui/icons-material/Search";
import { ref, push } from 'firebase/database';
import { database } from '../firebase';

interface FormData {
  name: string;
  email: string;
  phone: string;
  city: string;
  reason: string;
  isAlive: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
}

const IPEFSearch: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    city: '',
    reason: '',
    isAlive: ''
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const sanitizeInput = (input: string): string => {
    return input.replace(/[<>]/g, '').trim();
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters long';
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit Indian phone number';
      isValid = false;
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = 'City is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (contentContainerRef.current) {
        const contentRect = contentContainerRef.current.getBoundingClientRect();
        setIsSticky(contentRect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
      // Clear validation error when user starts typing
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: undefined
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before submitting.',
        severity: 'error'
      });
      return;
    }

    try {
      const ipefSearchRequestsRef = ref(database, 'ipefSearchRequests');
      const newIpefSearchRequest = {
        ...formData,
        createdOn: new Date().toISOString()
      };
      await push(ipefSearchRequestsRef, newIpefSearchRequest);

      setSnackbar({
        open: true,
        message: 'Request submitted successfully!',
        severity: 'success'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        reason: '',
        isAlive: ''
      });
      setValidationErrors({});
    } catch (error) {
      console.error('Error submitting IPEF search request:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting request. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div ref={contentContainerRef}>
      {/* Static Banner Section */}
      <Box
        sx={{
          width: "100%",
          height: "50vh",
          position: "relative",
          overflow: "hidden",
          background: `
            linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), 
            url(${"/assets/service_01.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Menu isSticky={isSticky} />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="h3" fontWeight="bold" letterSpacing={1}>
            IEPF Search
          </Typography>
        </Box>
      </Box>

      {/* Main Page Content */}
      <Box
        sx={{
          backgroundColor: "#F9FAFC",
          padding: "40px 20px",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid container spacing={4} sx={{ maxWidth: "1200px", boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)" }}>
          {/* Left Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "#2C387E",
              padding: "40px 20px",
              color: "#FFFFFF",
              borderRadius: "8px 0 0 8px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F57C00",
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                margin: "0 auto",
              }}
            >
              <SearchIcon sx={{ fontSize: 80, color: "#FFFFFF" }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                marginTop: "20px",
                fontWeight: "bold",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Book Your Slot Now!
            </Typography>
            <Typography
              variant="h4"
              sx={{
                marginTop: "10px",
                fontWeight: "bold",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Get Free IEPF Search
            </Typography>
            <Typography
              sx={{
                marginTop: "10px",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Share Samadhan has a dedicated team of IEPF Claim Experts including 50+ qualified
              professionals - CA, CS, Lawyers.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                marginTop: "20px",
                fontWeight: "bold",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              8800-33-2200
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              samadhan@sharesamadhan.com
            </Typography>
          </Grid>

          {/* Right Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "#FFFFFF",
              padding: "40px 20px",
              borderRadius: "0 8px 8px 0",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#2C387E",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Request a Call Back
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <TextField
                required
                name="name"
                label="Name"
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <TextField
                required
                name="email"
                label="Email"
                variant="outlined"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <TextField
                required
                name="phone"
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={formData.phone}
                onChange={handleChange}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone}
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ marginRight: "8px", color: "#000000" }}>+91</Typography>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <TextField
                required
                name="city"
                label="City Name"
                variant="outlined"
                fullWidth
                value={formData.city}
                onChange={handleChange}
                error={!!validationErrors.city}
                helperText={validationErrors.city}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <TextField
                required
                name="reason"
                label="Reason for Inquiry"
                select
                variant="outlined"
                fullWidth
                value={formData.reason}
                onChange={handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="Claim Inquiry">Claim Inquiry</MenuItem>
                <MenuItem value="General Inquiry">General Inquiry</MenuItem>
              </TextField>
              <TextField
                required
                name="isAlive"
                label="Is the shareholder alive?"
                select
                variant="outlined"
                fullWidth
                value={formData.isAlive}
                onChange={handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#2C387E",
                  color: "#FFFFFF",
                  padding: "12px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#1E2B6B",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default IPEFSearch;